/* eslint-disable max-lines-per-function */
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import { Dayjs } from 'dayjs';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { createStore } from 'zustand';
import { KalenderArbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/KalenderArbeitsbereich';
import { transformMitarbeiterStationenToArbeitsbereichStationen } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/utils/transformMitarbeiterStationenToArbeitsbereichStationen';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Abschnitt as RotationsplanAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import {
  Abschnitt,
  FilterOptions,
  KalenderView,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/types';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { filterRotationsplaene } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/filterRotationsplaene';
import { filterRotationsplaeneByArbeitsbereiche } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/filterRotationsplaeneByArbeitsbereiche';

interface RotationsplanFeatureProps {
  data: {
    rotationsplaene: Rotationsplan[];
    rotationsplaeneByArbeitsbereiche: KalenderArbeitsbereich[];
    abschnitte: Abschnitt[];
    berufe: Beruf[];
    standorte: Standort[];
  };
  ui: {
    year: string;
    kalender: {
      view: KalenderView;
      isScrolling: boolean;
      currentStartOfWeek: Dayjs;
      currentEndOfWeek: Dayjs;
      istSortierungAufsteigend: boolean;
      selectedFilterCount: number;
      filter: FilterOptions;
    };
  };
}

export interface RotationsplanFeatureState extends RotationsplanFeatureProps {
  setKalenderView: (view: KalenderView) => void;
  setKalenderScrolling: (isScrolling: boolean) => void;
  setKalenderSortierung: (istSortierungAufsteigend: boolean) => void;
  setCurrentStartOfWeek: (currentStartOfWeek: Dayjs) => void;
  setCurrentEndOfWeek: (currentEndOfWeek: Dayjs) => void;
  setBerufeFilter: (selectedBerufe: string[]) => void;
  setGliederungFilter: (selectedAbschnitte: Abschnitt[]) => void;
  setStandortFilter: (selectedStandorte: Standort[]) => void;
}

export type RotationsplanFeatureStore = ReturnType<
  typeof createRotationsplanFeatureStore
>;

export const createRotationsplanFeatureStore = (
  rotationsplaene: Rotationsplan[],
  berufe: Beruf[],
  standorte: Standort[],
  year: string,
) => {
  const defaultProps: RotationsplanFeatureProps = {
    data: {
      rotationsplaene: [],
      rotationsplaeneByArbeitsbereiche: [],
      abschnitte: [],
      berufe: [],
      standorte: [],
    },
    ui: {
      year,
      kalender: {
        isScrolling: false,
        view: 'byArbeitsbereiche',
        istSortierungAufsteigend: false,
        currentStartOfWeek: dayjs().year(parseInt(year)).startOf('isoWeek'),
        currentEndOfWeek: dayjs().year(parseInt(year)).endOf('isoWeek'),
        selectedFilterCount: 0,
        filter: {
          berufe: [],
          standorte: [],
          abschnitte: [],
        },
      },
    },
  };

  const rotationsplaeneByArbeitsbereiche =
    transformMitarbeiterStationenToArbeitsbereichStationen(rotationsplaene);

  const abschnitte = rotationsplaene
    .flatMap((plan) => plan.abschnitte)
    .reduce((abschnitte, current): RotationsplanAbschnitt[] => {
      const names = abschnitte.map((a) => a.position + '. ' + a.name);
      if (!names.includes(current.position + '. ' + current.name)) {
        abschnitte.push(current);
      }
      return abschnitte;
    }, [])
    .sort((a, b) => {
      // Lehrjahr kommt immer zuerst
      if (a.name === 'Lehrjahr' && b.name !== 'Lehrjahr') return -1;
      if (a.name !== 'Lehrjahr' && b.name === 'Lehrjahr') return 1;

      // dann nach Position vergleichen
      if (a.position !== b.position) {
        return a.position - b.position;
      }

      // am Ende nach Name vergleichen
      return a.name.localeCompare(b.name);
    })
    .map((abschnitt) => ({
      name: `${abschnitt.position}. ${abschnitt.name}`,
      value: abschnitt.id,
    }));

  return createStore<RotationsplanFeatureState>()((set) => ({
    data: {
      rotationsplaene,
      rotationsplaeneByArbeitsbereiche,
      abschnitte,
      berufe,
      standorte,
    },
    ui: {
      ...defaultProps.ui,
    },
    setKalenderView: (view) =>
      set((state) => ({
        ui: { ...state.ui, kalender: { ...state.ui.kalender, view } },
      })),
    setKalenderSortierung: (istSortierungAufsteigend) =>
      set((state) => ({
        data: {
          ...state.data,
          rotationsplaene: [...state.data.rotationsplaene].sort((a, b) => {
            if (istSortierungAufsteigend) {
              return a.mitarbeiter.nachname.localeCompare(
                b.mitarbeiter.nachname,
              );
            } else {
              return b.mitarbeiter.nachname.localeCompare(
                a.mitarbeiter.nachname,
              );
            }
          }),
          rotationsplaeneByArbeitsbereiche: [
            ...state.data.rotationsplaeneByArbeitsbereiche,
          ].sort((a, b) => {
            if (istSortierungAufsteigend) {
              return a.name.localeCompare(b.name);
            } else {
              return b.name.localeCompare(a.name);
            }
          }),
        },
        ui: {
          ...state.ui,
          kalender: { ...state.ui.kalender, istSortierungAufsteigend },
        },
      })),
    setKalenderScrolling: (isScrolling) =>
      set((state) => ({
        ui: {
          ...state.ui,
          kalender: { ...state.ui.kalender, isScrolling },
        },
      })),
    setCurrentStartOfWeek: (currentStartOfWeek) =>
      set((state) => ({
        ui: {
          ...state.ui,
          kalender: { ...state.ui.kalender, currentStartOfWeek },
        },
      })),
    setCurrentEndOfWeek: (currentEndOfWeek) =>
      set((state) => ({
        ui: {
          ...state.ui,
          kalender: { ...state.ui.kalender, currentEndOfWeek },
        },
      })),
    setBerufeFilter: (selectedBerufe) =>
      set((state) => ({
        data: {
          ...state.data,
          rotationsplaeneByArbeitsbereiche:
            filterRotationsplaeneByArbeitsbereiche(
              'berufe',
              selectedBerufe,
              state.ui.kalender.filter,
              rotationsplaeneByArbeitsbereiche,
            ),
          rotationsplaene: filterRotationsplaene(
            'berufe',
            selectedBerufe,
            state.ui.kalender.filter,
            rotationsplaene,
          ),
        },
        ui: {
          ...state.ui,
          kalender: {
            ...state.ui.kalender,
            filter: {
              ...state.ui.kalender.filter,
              berufe: [...selectedBerufe],
            },
          },
        },
      })),
    setGliederungFilter: (selectedAbschnitte) => {
      return set((state) => ({
        data: {
          ...state.data,
          rotationsplaeneByArbeitsbereiche:
            filterRotationsplaeneByArbeitsbereiche(
              'abschnitte',
              selectedAbschnitte,
              state.ui.kalender.filter,
              rotationsplaeneByArbeitsbereiche,
            ),
          rotationsplaene: filterRotationsplaene(
            'abschnitte',
            selectedAbschnitte,
            state.ui.kalender.filter,
            rotationsplaene,
          ),
        },
        ui: {
          ...state.ui,
          kalender: {
            ...state.ui.kalender,
            filter: {
              ...state.ui.kalender.filter,
              abschnitte: [...selectedAbschnitte],
            },
          },
        },
      }));
    },
    setStandortFilter: (selectedStandorte) => {
      return set((state) => ({
        data: {
          ...state.data,
          rotationsplaeneByArbeitsbereiche:
            filterRotationsplaeneByArbeitsbereiche(
              'standorte',
              selectedStandorte,
              state.ui.kalender.filter,
              rotationsplaeneByArbeitsbereiche,
            ),
          rotationsplaene: filterRotationsplaene(
            'standorte',
            selectedStandorte,
            state.ui.kalender.filter,
            rotationsplaene,
          ),
        },
        ui: {
          ...state.ui,
          kalender: {
            ...state.ui.kalender,
            filter: {
              ...state.ui.kalender.filter,
              standorte: [...selectedStandorte],
            },
          },
        },
      }));
    },
  }));
};
